import AgentIcon from "../AgentIcon";
import AgentChatMessage from "./AgentChatMessage";
import UserChatMessage from "./UserChatMessage";
import type { AgentChatMode, AgentChatSettings } from "@app/state/types";
import type { ChatMessage } from "@lib/agent/types";
import Icon from "@lib/common/components/Icon/Icon";
import formatChatDate from "@lib/common/utils/formatChatDate";
import ChatBox from "@lib/experiment/agent/ExperimentDesignChatBox/ChatBox";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Group,
  Menu,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  Transition
} from "@mantine/core";
import { useThrottledState } from "@mantine/hooks";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type AgentChatProps = {
  messages: ChatMessage[];
  isStreaming: boolean;
  isReady: boolean;
  send: (content: string, files?: File[]) => void;
  sessionAttachmentCount?: number;
  /** True while the persisted message history is loading on first mount. */
  isHistoryLoading?: boolean;
  /** True when the history bootstrap query failed; renders an inline retry banner. */
  historyError?: boolean;
  disabledClear?: boolean;
  userFeedback?: ReactNode;
  onRetryHistory?: () => void;
  onClear: VoidFunction;
  variant?: "draft" | "experiment";
} & Pick<AgentChatSettings, "chatMode"> & {
    setChatMode: (mode: AgentChatMode) => void;
    toggle: (value: boolean) => void;
  };

export default function AgentChat({
  messages,
  isStreaming,
  isReady,
  send,
  sessionAttachmentCount = 0,
  isHistoryLoading = false,
  historyError = false,
  onRetryHistory,
  onClear,
  disabledClear,
  userFeedback,
  variant = "experiment",
  chatMode,
  setChatMode,
  toggle
}: AgentChatProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const [scrollPosition, setScrollPosition] = useThrottledState<{ x: number; y: number }>({ x: 0, y: 0 }, 500);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const scrollToBottom = useCallback(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTo({ top: viewportRef.current.scrollHeight, behavior: "smooth" });
    }
    // Re-focus chat input.
    chatInputRef.current?.focus();
  }, []);

  // Auto-scroll on new messages or streaming content, but only when the user
  // is already near the bottom — otherwise scrolling up to re-read would be
  // yanked back by every streamed chunk.
  // useEffect(() => {
  //   const el = viewportRef.current;
  //   if (!el) {
  //     return;
  //   }
  //   const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
  //   // Auto-scroll to bottom if near bottom or at the top.
  //   if (nearBottom || (el.scrollTop === 0 && el.scrollHeight > el.clientHeight)) {
  //     scrollToBottom();
  //   }
  // }, [messages, scrollToBottom]);

  // Toggle Scroll-to-bottom button on scroll change.
  useEffect(() => {
    if (viewportRef.current) {
      setShowScrollToBottom(
        viewportRef.current.scrollHeight - scrollPosition.y > viewportRef.current.clientHeight + 50
      );
    }
  }, [scrollPosition]);

  // Focus chat input when connected
  useEffect(() => {
    if (isReady) {
      chatInputRef.current?.focus();
      scrollToBottom();
    }
  }, [isReady, scrollToBottom]);

  const formattedDate = useMemo(() => formatChatDate(new Date()), []);

  const lastAssistantExtras = [...messages].reverse().find(m => m.role === "assistant" && m.extras)?.extras;
  const lastOpenQuestion =
    lastAssistantExtras && [...lastAssistantExtras].reverse().find(item => item.action === "question_open");
  const defaultPlaceholder = variant === "draft" ? "Describe your experiment..." : "Ask about this experiment...";
  const inputPlaceholder =
    lastOpenQuestion?.action === "question_open" && lastOpenQuestion.input_placeholder
      ? lastOpenQuestion.input_placeholder
      : defaultPlaceholder;

  const disabled = useMemo(
    () => !isReady || isHistoryLoading || historyError,
    [isReady, isHistoryLoading, historyError]
  );
  const showEmptyState = !isHistoryLoading && !historyError && messages.length === 0;

  return (
    <Stack h="100%" gap={0}>
      <Group px="xs" align="center" gap="md" justify="space-between" pb={5} wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <AgentIcon size="1.5rem" />
          <Text size="xs" fw={500} lh={1} truncate>
            AI chat · {formattedDate}
          </Text>
        </Group>
        <Group gap={5} wrap="nowrap">
          <Tooltip label="Clear conversation" disabled={disabledClear}>
            <ActionIcon onClick={onClear} disabled={disabledClear}>
              <Icon name="trash" size="0.9rem" />
            </ActionIcon>
          </Tooltip>

          <Menu width={150}>
            <Menu.Target>
              <Tooltip label="Switch chat mode">
                <ActionIcon>
                  <Icon name="sidebar-flip" size="1rem" />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => setChatMode("sidebar")}
                leftSection={<Icon name="sidebar-flip" color="dimmed" size="0.8rem" />}
                rightSection={chatMode === "sidebar" ? <Icon name="check" /> : undefined}
              >
                Sidebar
              </Menu.Item>
              <Menu.Item
                onClick={() => setChatMode("floating")}
                leftSection={<Icon name="window-restore" color="dimmed" size="0.8rem" />}
                rightSection={chatMode === "floating" ? <Icon name="check" /> : undefined}
              >
                Floating
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Tooltip label="Hide chat">
            <ActionIcon onClick={() => toggle(false)}>
              <Icon name="xmark" size="1rem" variant="solid" />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      <ScrollArea viewportRef={viewportRef} onScrollPositionChange={setScrollPosition} flex={1} scrollbarSize={2}>
        <Box p="sm" pb="5rem" pr="sm">
          <Stack gap="md" py="md" style={{ display: "flex", flexDirection: "column" }}>
            {historyError && (
              <Center>
                <Stack gap="sm" align="center" mt="lg" maw={400}>
                  <Text c="dimmed" ta="center">
                    Couldn&apos;t load the conversation history.
                  </Text>
                  {onRetryHistory && (
                    <Button variant="light" size="xs" onClick={onRetryHistory}>
                      Retry
                    </Button>
                  )}
                </Stack>
              </Center>
            )}
            {isHistoryLoading && (
              <Stack gap="md" py="md">
                <Skeleton height={48} width="60%" radius="md" />
                <Skeleton height={64} width="80%" radius="md" ml="auto" />
                <Skeleton height={48} width="55%" radius="md" />
              </Stack>
            )}
            {showEmptyState && (
              <Center>
                <Box mt="20vh" maw={650}>
                  <Group gap="xl" wrap="nowrap" align="center" pos="relative">
                    <Icon name="clipboard-list-check" size="xxl" variant="light" style={{ opacity: 0.2 }} />
                    <Stack gap="md">
                      {variant === "draft" ? (
                        <Stack gap="sm">
                          <Text style={{ textWrap: "balance" }}>
                            Describe your experiment and I&apos;ll fill in the draft next to this chat.
                          </Text>
                          <Text style={{ textWrap: "balance" }}>
                            You can also edit any field by hand — we stay in sync. When you&apos;re ready, click{" "}
                            <b>Create experiment</b> above to publish.
                          </Text>
                        </Stack>
                      ) : (
                        <Stack gap="sm">
                          <Text style={{ textWrap: "balance" }}>
                            Ask about this experiment&apos;s recommendations, model status, or expert context.
                          </Text>
                          <Text style={{ textWrap: "balance" }}>
                            I can also help you edit the expert context or rename the experiment.
                          </Text>
                        </Stack>
                      )}
                    </Stack>
                  </Group>
                </Box>
              </Center>
            )}
            {messages.map((message, i) =>
              message.role === "user" ? (
                <UserChatMessage message={message} key={message.id} />
              ) : (
                <AgentChatMessage
                  key={message.id}
                  message={message}
                  isStreaming={isStreaming && i === messages.length - 1}
                  onSend={send}
                />
              )
            )}
          </Stack>
        </Box>
      </ScrollArea>
      {userFeedback ? <Center mb={5}>{userFeedback}</Center> : undefined}
      <Box px="xs" pos="relative">
        <Transition mounted={showScrollToBottom} transition="pop" enterDelay={200}>
          {transitionStyles => (
            <Center pos="absolute" left={0} right={0} top={-60}>
              <ActionIcon
                style={{
                  ...transitionStyles,
                  boxShadow: "var(--mantine-shadow-lg)",
                  border: "solid 1px var(--mantine-color-default-border)"
                }}
                radius="xl"
                onClick={scrollToBottom}
                bg="var(--mantine-color-default)"
              >
                <Icon name="arrow-down" size="0.8rem" color="dimmed" />
              </ActionIcon>
            </Center>
          )}
        </Transition>

        <ChatBox
          ref={chatInputRef}
          onSubmit={send}
          disabled={disabled}
          isStreaming={isStreaming}
          enableAttachments
          sessionAttachmentCount={sessionAttachmentCount}
          textareaProps={{
            placeholder: inputPlaceholder,
            maxRows: 6
          }}
        />
        <Center>
          <Group gap="xs" mt={7} align="center">
            <Text c="dimmed" size="xs">
              The AI can make mistakes. Verify important information.
            </Text>
          </Group>
        </Center>
      </Box>
    </Stack>
  );
}
